import { useEffect, useRef, useState } from "react";

/**
 * When does the SPLASH actually appear — the design's "no flash" definition.
 *
 * The app boots by resolving the session and the store binding. On a fast
 * connection that is a few dozen milliseconds, and painting a full-screen
 * illustration for that long reads as a flicker, not as polish. So:
 *
 * - nothing is painted until the boot has been pending for `showAfterMs`
 *   (the bare background is already there);
 * - once painted, it stays for at least `minVisibleMs`, so it can't blink off
 *   the instant it came on.
 *
 * `show` is whether the caller should still be rendering the boot screen at
 * all (pending, or being held); `visible` is whether the art is painted yet.
 *
 * **It gates the FIRST resolution only.** Once `pending` has been seen false
 * (the boot resolved — to a session OR to an error), it is ignored for good.
 * That is not tidiness: a boot that resolved to a FAILED read renders an error
 * screen whose mounting subscribers retry the errored query; the retry flips
 * `pending` back to true, which — if it brought the splash back — unmounted
 * that screen, which resolved to the error again, which mounted it again…
 * an unbounded request loop against a server that was already struggling
 * (found with the API down: thousands of `/seller/auth/me` per second and a
 * blank page). A user's own "Try again" is the error screen's to show.
 */
export const SPLASH_SHOW_AFTER_MS = 250;
export const SPLASH_MIN_VISIBLE_MS = 700;

export function useSplashGate(
  pending: boolean,
  showAfterMs: number = SPLASH_SHOW_AFTER_MS,
  minVisibleMs: number = SPLASH_MIN_VISIBLE_MS,
): { show: boolean; visible: boolean } {
  const [visible, setVisible] = useState(false);
  const [holding, setHolding] = useState(false);
  const shownAt = useRef<number | null>(null);
  // Latched in STATE, not a ref: what this render returns depends on it, and
  // a ref read during render is invisible to React (react-hooks/refs). Set
  // during render the documented way — conditionally, on a prop change — so
  // the latch takes effect in the same pass with no extra commit.
  const [settled, setSettled] = useState(!pending);
  if (!pending && !settled) setSettled(true);
  const live = pending && !settled;

  useEffect(() => {
    if (live) {
      if (visible) return;
      const id = setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
        setHolding(true);
      }, showAfterMs);
      return () => clearTimeout(id);
    }
    if (visible && holding) {
      const elapsed = Date.now() - (shownAt.current ?? Date.now());
      const id = setTimeout(
        () => setHolding(false),
        Math.max(0, minVisibleMs - elapsed),
      );
      return () => clearTimeout(id);
    }
    return undefined;
  }, [live, visible, holding, showAfterMs, minVisibleMs]);

  return { show: live || holding, visible };
}
